import { ChangeDetectionStrategy, Component, ChangeDetectorRef, input, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25ToggleButtonModule } from "../../s25-toggle-button/s25.toggle.button.module";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { Item } from "../../../pojo/Item";
import type { ObjectOccurrence } from "../data.ingestion.service";
import { fetchLocationResultsFromSearch, type LocationSearchResult } from "./object.occurrence.availability";
import { OBJECT_OCCURRENCE_SEARCH_LAYOUT_STYLES, ObjectOccurrenceSearchBase } from "./object.occurrence.search.base";

@TypeManagerDecorator("s25-ng-object-occurrence-location-search")
@Component({
    selector: "s25-ng-object-occurrence-location-search",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, S25DropdownPaginatedModule, S25ToggleButtonModule, S25ButtonComponent],
    template: `
        <div class="location-selection-per-occurrence">
            <div class="search-controls">
                <div class="search-method-selector">
                    <span class="search-method-label">Search method</span>
                    <s25-toggle-button
                        [value]="searchMethod() === 'name'"
                        (valueChange)="onSearchMethodChange($event ? 'name' : 'saved')"
                        [falseLabel]="'Saved search'"
                        [trueLabel]="'Search by name'"
                        [toggleLabel]="'Location search method'"
                    />
                </div>

                @if (searchMethod() === "saved") {
                    <div class="search-dropdown-section">
                        <label>Select Saved Search:</label>
                        <s25-ng-search-dropdown
                            [itemTypeId]="Item.Ids.Location"
                            [onSelect]="onSearchSelected"
                            [value]="selectedSearch()"
                        >
                        </s25-ng-search-dropdown>
                        @if (selectedSearch() !== undefined) {
                            <s25-ng-button [type]="'outline'" [onClick]="clearSearchSelection" class="clear-search-btn">
                                Clear
                            </s25-ng-button>
                        }
                    </div>
                }

                @if (searchMethod() === "name") {
                    <div class="search-text-section">
                        <label>Enter Location Name:</label>
                        <div class="text-search-input-group">
                            <input
                                type="text"
                                class="c-input text-search-input"
                                [ngModel]="nameSearchText()"
                                (ngModelChange)="nameSearchText.set($event)"
                                (keyup.enter)="searchByName()"
                                placeholder="Enter location name..."
                            />
                            <s25-ng-button [type]="'primary'" [onClick]="searchByName" class="search-btn"
                                >Search</s25-ng-button
                            >
                        </div>
                    </div>
                }
            </div>

            @if (searching()) {
                <div class="search-loading">Searching locations...</div>
            }

            @if (locationResults().length > 0) {
                <div class="location-results">
                    <h5>Available Locations</h5>
                    <ul class="search-results-list">
                        @for (result of locationResults(); track result.locationId) {
                            <li class="search-result-item">
                                <div class="location-info">
                                    <span class="location-name">{{ result.locationName }}</span>
                                    <span class="availability-badge"
                                        >({{ result.availableCount }}/{{ totalOccurrences() }})</span
                                    >
                                </div>
                                @if (result.availableOccurrenceUuids.includes(occurrenceUuid())) {
                                    <s25-ng-button
                                        [type]="'primary'"
                                        [onClick]="pickLocation"
                                        [onClickArgs]="[result]"
                                        class="add-location-btn"
                                    >
                                        Add
                                    </s25-ng-button>
                                }
                            </li>
                        }
                    </ul>
                </div>
            } @else if (hasSearched() && !searching()) {
                <div class="no-results">No available locations found</div>
            }
        </div>
    `,
    styles: [
        OBJECT_OCCURRENCE_SEARCH_LAYOUT_STYLES,
        `
            .location-selection-per-occurrence {
                margin-top: 0.75rem;
                padding: 0.75rem;
                background-color: #f0f8ff;
                border-radius: 4px;
                border: 1px solid #b0d4ff;
            }
        `,
    ],
})
export class S25ObjectOccurrenceLocationSearchComponent extends ObjectOccurrenceSearchBase {
    readonly locationPicked = output<{ locationId: number; locationName: string }>();

    readonly Item = Item;

    readonly locationResults = signal<LocationSearchResult[]>([]);

    constructor(cd: ChangeDetectorRef) {
        super(cd);
    }

    protected override resetSearchResults(): void {
        this.locationResults.set([]);
    }

    protected override rewriteSavedSearchQuery(searchQuery: string): string {
        return searchQuery.replace("&spaces_", "&");
    }

    protected override async runSearch(searchQuery: string): Promise<void> {
        this.searching.set(true);
        this.hasSearched.set(false);

        try {
            const results = await fetchLocationResultsFromSearch(
                searchQuery,
                this.occurrences(),
                this.existingObjectIds(),
            );
            this.locationResults.set(results);
            this.hasSearched.set(true);
        } catch (error) {
            console.error("Error fetching locations:", error);
            this.locationResults.set([]);
            this.hasSearched.set(true);
        } finally {
            this.searching.set(false);
            this.cd.markForCheck();
        }
    }

    protected override emptyNameSearchAlert(): string {
        return "Please enter a location name to search";
    }

    protected override buildNameSearchQuery(trimmedName: string): string {
        return `&name=${encodeURIComponent(trimmedName)}`;
    }

    readonly pickLocation = (result: LocationSearchResult) => {
        if (!result.availableOccurrenceUuids.includes(this.occurrenceUuid())) {
            return;
        }
        this.locationPicked.emit({ locationId: result.locationId, locationName: result.locationName });
        this.locationResults.update((list) => list.filter((r) => r.locationId !== result.locationId));
        this.cd.markForCheck();
    };
}
